import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import AdminDashboard from "@/pages/AdminDashboard";
import CoordinatorDashboard from "@/pages/CoordinatorDashboard";
import Schools from "@/pages/Schools";
import Opportunities from "@/pages/Opportunities";
import Volunteers from "@/pages/Volunteers";
import Gallery from "@/pages/Gallery";
import Reports from "@/pages/Reports";
import Statistics from "@/pages/Statistics";
import HonorBoard from "@/pages/HonorBoard";
import Challenges from "@/pages/Challenges";
import Suggestions from "@/pages/Suggestions";
import Resources from "@/pages/Resources";
import Users from "@/pages/Users";
import SettingsPage from "@/pages/SettingsPage";
import Pledges from "@/pages/Pledges";
import Instructions from "@/pages/Instructions";
import MonthlyCertificates from "@/pages/MonthlyCertificates";
import CoordinatorCertificates from "@/pages/CoordinatorCertificates";
import Circulars from "@/pages/Circulars";
import MySchools from "@/pages/MySchools";
import SupervisorsOversight from "@/pages/SupervisorsOversight";
import Snapshots from "@/pages/Snapshots";
import SchoolDistribution from "@/pages/SchoolDistribution";

function Loader() {
  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="w-14 h-14 rounded-full border-4 border-amber-400/20 border-t-amber-400 animate-spin" />
    </div>
  );
}

function Protected({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <Loader />;
  if (!user) return <Navigate to="/login" replace />;
  return <Layout>{children}</Layout>;
}

function Dashboard() {
  const { user } = useAuth();
  return user?.role === "admin" ? <AdminDashboard /> : <CoordinatorDashboard />;
}

function AppRoutes() {
  const { user, loading } = useAuth();
  return (
    <Routes>
      <Route path="/login" element={loading ? <Loader /> : user ? <Navigate to="/" replace /> : <Login />} />
      <Route path="/" element={<Protected><Dashboard /></Protected>} />
      <Route path="/schools" element={<Protected><Schools /></Protected>} />
      <Route path="/opportunities" element={<Protected><Opportunities /></Protected>} />
      <Route path="/volunteers" element={<Protected><Volunteers /></Protected>} />
      <Route path="/students" element={<Protected><Volunteers fixedType="student" /></Protected>} />
      <Route path="/students/first" element={<Protected><Volunteers fixedType="student" fixedGrade="أول ثانوي" /></Protected>} />
      <Route path="/students/second" element={<Protected><Volunteers fixedType="student" fixedGrade="ثاني ثانوي" /></Protected>} />
      <Route path="/students/third" element={<Protected><Volunteers fixedType="student" fixedGrade="ثالث ثانوي" /></Protected>} />
      <Route path="/staff" element={<Protected><Volunteers fixedType="staff" /></Protected>} />
      <Route path="/gallery" element={<Protected><Gallery /></Protected>} />
      <Route path="/reports" element={<Protected><Reports /></Protected>} />
      <Route path="/statistics" element={<Protected><Statistics /></Protected>} />
      <Route path="/honor" element={<Protected><HonorBoard /></Protected>} />
      <Route path="/challenges" element={<Protected><Challenges /></Protected>} />
      <Route path="/suggestions" element={<Protected><Suggestions /></Protected>} />
      <Route path="/resources" element={<Protected><Resources /></Protected>} />
      <Route path="/users" element={<Protected><Users /></Protected>} />
      <Route path="/settings" element={<Protected><SettingsPage /></Protected>} />
      <Route path="/pledges" element={<Protected><Pledges /></Protected>} />
      <Route path="/instructions" element={<Protected><Instructions /></Protected>} />
      <Route path="/monthly-certificates" element={<Protected><MonthlyCertificates /></Protected>} />
      <Route path="/my-certificates" element={<Protected><CoordinatorCertificates /></Protected>} />
      <Route path="/circulars" element={<Protected><Circulars /></Protected>} />
      <Route path="/my-schools" element={<Protected><MySchools /></Protected>} />
      <Route path="/supervisors-oversight" element={<Protected><SupervisorsOversight /></Protected>} />
      <Route path="/supervisors-oversight/:supId" element={<Protected><MySchools /></Protected>} />
      <Route path="/snapshots" element={<Protected><Snapshots /></Protected>} />
      <Route path="/school-distribution" element={<Protected><SchoolDistribution /></Protected>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Toaster position="top-center" richColors theme="light" toastOptions={{ style: { fontFamily: "Cairo, sans-serif", direction: "rtl" } }} />
          <AppRoutes />
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}
